import { DocumentPreviewPane } from '@/components/features/document-list-preview-pane'
import { IndexPreviewEmpty } from '@/components/integrations/_shared/index-preview-empty'
import type { SelectedItem } from '@/components/features/document-list.types'
import { HomeSummaryPreview } from '@/components/dashboard/home-summary-preview'
import { BasecampTodoPreviewPane } from '@/components/integrations/basecamp/basecamp-todo-preview-pane'
import {
  documentIdFromHomeSelection,
  pathFromHomeSelection,
  titleFromHomeSelection,
  type HomeSelection,
} from '@/components/dashboard/home-selection'
import { testIds } from '@/lib/test-ids'

export type HomePreviewPaneProps = {
  selection: HomeSelection | null
  onOpen: (event?: React.MouseEvent) => void
  onClose: () => void
  workspaceColor?: string
}

/**
 * Home PanelRight content: DocumentPreviewPane when a document UUID is available,
 * BasecampTodoPreviewPane for bookmarked todos, otherwise a compact summary card.
 */
export function HomePreviewPane({
  selection,
  onOpen,
  onClose,
  workspaceColor,
}: HomePreviewPaneProps) {
  if (!selection) {
    return (
      <IndexPreviewEmpty
        row
        noun="row"
        onClose={onClose}
        testId={testIds.home.previewEmpty}
      />
    )
  }

  if (selection.kind === 'basecamp-todo') {
    const assignment = selection.assignment
    const workSpecStatus =
      assignment._workspec_status && assignment._workspec_file_path
        ? { status: assignment._workspec_status, filePath: assignment._workspec_file_path }
        : null
    const lastComment =
      assignment._last_comment_text
        ? {
            text: assignment._last_comment_text,
            createdAt: assignment._last_comment_at || '',
            creatorName: assignment._last_comment_creator || undefined,
          }
        : null
    return (
      <div className="h-full min-h-0" data-testid={testIds.home.previewPane}>
        <BasecampTodoPreviewPane
          assignment={assignment}
          workSpecStatus={workSpecStatus}
          lastComment={lastComment}
          onOpen={onOpen}
          onClose={onClose}
          onOpenBasecamp={(url) => window.open(url, '_blank', 'noopener,noreferrer')}
        />
      </div>
    )
  }

  const documentId = documentIdFromHomeSelection(selection)
  if (documentId) {
    const item: SelectedItem = {
      kind: 'document',
      doc: {
        id: documentId,
        title: titleFromHomeSelection(selection),
        type: 'note',
        excerpt: '',
        updatedAt: '',
        updatedBy: '',
        tags: [],
        filePath: pathFromHomeSelection(selection),
      },
    }
    return (
      <div className="h-full min-h-0" data-testid={testIds.home.previewPane}>
        <DocumentPreviewPane
          item={item}
          workspaceColor={workspaceColor}
          onOpen={onOpen}
          onClose={onClose}
        />
      </div>
    )
  }

  return (
    <div className="h-full min-h-0" data-testid={testIds.home.previewPane}>
      <HomeSummaryPreview selection={selection} onOpen={onOpen} onClose={onClose} />
    </div>
  )
}
